import { useState, useEffect, useCallback } from 'react';
import { hrms, salesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../theme';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';

// ─── Constants ───────────────────────────────────────────────────────────────
const DAY_LABELS   = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOUR_OPTIONS = [0.5,1,1.5,2,2.5,3,3.5,4,4.5,5,5.5,6,6.5,7,7.5,8,8.5,9,9.5,10,10.5,11,11.5,12];
const SALES_ROLES  = ['sales_head', 'sales_executive', 'super_admin'];
const PERIODS      = [
  { key: 'daily',   label: 'Today' },
  { key: 'weekly',  label: 'This Week' },
  { key: 'monthly', label: 'This Month' },
  { key: 'yearly',  label: 'This Year' },
  { key: 'custom',  label: 'Custom' },
];
const PALETTE = ['#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6','#14B8A6','#F97316','#6366F1','#EC4899','#6B7280'];

// ─── Helpers ─────────────────────────────────────────────────────────────────
function getWeekDates(anchor) {
  const base = anchor ? new Date(anchor) : new Date();
  const day  = base.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(base);
  monday.setDate(base.getDate() + diff);
  monday.setHours(0, 0, 0, 0);
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

const toISO = d => {
  if (!(d instanceof Date)) return d;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const fmt   = n  => Number(n).toFixed(1);

const TH = { padding: '0.65rem 0.6rem', color: '#D1D5DB', fontSize: '0.8rem', fontWeight: 600, textAlign: 'center', border: 'none', whiteSpace: 'nowrap' };
const TD = { padding: '0.55rem 0.6rem', fontSize: '0.85rem', border: 'none', verticalAlign: 'middle' };

// ─── LogHours tab ─────────────────────────────────────────────────────────────
function LogHours({ user }) {
  const isSales = SALES_ROLES.includes(user?.role);
  const [weekOffset, setWeekOffset] = useState(0);

  const weekDates = getWeekDates(
    weekOffset === 0 ? null : (() => { const d = new Date(); d.setDate(d.getDate() + weekOffset * 7); return d; })()
  );
  const weekStart = toISO(weekDates[0]);

  const [rows,        setRows]        = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [allTenders,  setAllTenders]  = useState([]);
  const [leaveDays,   setLeaveDays]   = useState(new Set()); // ISO date strings of approved leave
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [saveMsg,     setSaveMsg]     = useState('');
  const [error,       setError]       = useState('');
  const [showAdd,     setShowAdd]     = useState(false);
  const [addTab,      setAddTab]      = useState('project');
  const [search,      setSearch]      = useState('');
  const [weekStatus,  setWeekStatus]  = useState(null); // {status, submitted_at, rejection_reason}

  // Allow editing up to 60 days back; future weeks locked
  const isLocked = (() => {
    const today = new Date(); today.setHours(0,0,0,0);
    const saturday = weekDates[5];
    const sixtyDaysAgo = new Date(today); sixtyDaysAgo.setDate(today.getDate() - 60);
    return saturday < sixtyDaysAgo || weekDates[0] > today;
  })();

  useEffect(() => {
    setLoading(true); setError(''); setRows([]);
    const calls = [hrms.getTimesheetWeek(weekStart), hrms.getProjects(), hrms.getWeekStatus(weekStart)];
    if (isSales) calls.push(salesAPI.getTenders({ limit: 200 }));
    Promise.all(calls).then(([tsRes, projRes, statusRes, tenderRes]) => {
      const data = tsRes.data;
      setAllProjects(projRes.data || []);
      setWeekStatus(statusRes.data || { status: 'draft' });
      setLeaveDays(new Set(data.leave_days || []));
      if (tenderRes) setAllTenders(tenderRes.data?.tenders || []);
      const rowMap = {};
      for (const ap of data.assigned_projects || []) {
        const key = `p-${ap.project_id}`;
        rowMap[key] = { key, row_type: 'project', project_id: ap.project_id, project_name: ap.project_name, project_code: ap.project_code, isAssigned: true, hours: {} };
      }
      for (const e of data.entries || []) {
        if (e.row_type === 'tender' && e.tender_id) {
          const key = `t-${e.tender_id}`;
          if (!rowMap[key]) rowMap[key] = { key, row_type: 'tender', tender_id: e.tender_id, tender_ref: e.tender_ref, tender_title: e.tender_title, isAssigned: false, hours: {} };
          rowMap[key].hours[e.date] = e.hours;
        } else {
          const key = e.project_id ? `p-${e.project_id}` : `custom-${e.project_name}`;
          if (!rowMap[key]) rowMap[key] = { key, row_type: 'project', project_id: e.project_id, project_name: e.project_name, project_code: e.project_code, isAssigned: false, hours: {} };
          rowMap[key].hours[e.date] = e.hours;
        }
      }
      setRows(Object.values(rowMap));
    }).catch(() => setError('Failed to load timesheet data.'))
      .finally(() => setLoading(false));
  }, [weekStart]); // eslint-disable-line

  const updateHours = (key, dateStr, val) => {
    setRows(prev => prev.map(r => r.key === key ? { ...r, hours: { ...r.hours, [dateStr]: val } } : r));
    setSaveMsg('');
  };
  const addProjectRow = p => {
    const key = `p-${p.id}`;
    if (!rows.find(r => r.key === key)) setRows(prev => [...prev, { key, row_type: 'project', project_id: p.id, project_name: p.name, project_code: p.code, isAssigned: false, hours: {} }]);
    setShowAdd(false); setSearch('');
  };
  const addTenderRow = t => {
    const key = `t-${t.id}`;
    if (!rows.find(r => r.key === key)) setRows(prev => [...prev, { key, row_type: 'tender', tender_id: t.id, tender_ref: t.reference_number || t.tender_ref, tender_title: t.title, isAssigned: false, hours: {} }]);
    setShowAdd(false); setSearch('');
  };
  const removeRow = key => setRows(prev => prev.filter(r => r.key !== key));

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const entries = rows.flatMap(row =>
        weekDates.map(d => ({
          row_type: row.row_type || 'project',
          project_id: row.row_type === 'project' ? row.project_id : null,
          project_name: row.row_type === 'project' ? row.project_name : null,
          project_code: row.row_type === 'project' ? row.project_code : null,
          tender_id: row.row_type === 'tender' ? row.tender_id : null,
          tender_ref: row.row_type === 'tender' ? row.tender_ref : null,
          tender_title: row.row_type === 'tender' ? row.tender_title : null,
          date: toISO(d), hours: Number(row.hours[toISO(d)] || 0),
        }))
      );
      const res = await hrms.saveTimesheet({ week_start: weekStart, entries });
      setSaveMsg('Saved successfully!');
      setWeekStatus(ws => ({ ...ws, status: ws?.status === 'approved' ? 'approved' : 'draft', total_hours: res.data?.total_hours }));
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to save timesheet.');
    } finally { setSaving(false); }
  };

  const handleSubmit = async () => {
    if (!window.confirm('Submit this week\'s timesheet for PM approval?')) return;
    setSubmitting(true); setError('');
    try {
      await hrms.submitTimesheet(weekStart);
      setWeekStatus(ws => ({ ...ws, status: 'submitted' }));
      setSaveMsg('Timesheet submitted for approval!');
      setTimeout(() => setSaveMsg(''), 4000);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to submit timesheet.');
    } finally { setSubmitting(false); }
  };

  const rowTotal   = row    => weekDates.reduce((s, d) => s + (Number(row.hours[toISO(d)]) || 0), 0);
  const dayTotal   = ds     => rows.reduce((s, r) => s + (Number(r.hours[ds]) || 0), 0);
  const grandTotal = rows.reduce((s, r) => s + rowTotal(r), 0);
  const today = new Date(); today.setHours(0,0,0,0);
  const isOver40h = grandTotal > 40;
  const wsStatus = weekStatus?.status || 'draft';
  const isApproved = wsStatus === 'approved';
  const isSubmitted = wsStatus === 'submitted';

  // Submit window: opens Friday 6 PM
  const canSubmitNow = (() => {
    const now = new Date();
    const saturday = new Date(weekDates[5]); saturday.setHours(23, 59, 59, 999);
    if (now > saturday) return true; // past week — always allow
    const friday6pm = new Date(weekDates[4]); friday6pm.setHours(18, 0, 0, 0);
    return now >= friday6pm;
  })();

  const submitOpensAt = (() => {
    const friday6pm = new Date(weekDates[4]); friday6pm.setHours(18, 0, 0, 0);
    return friday6pm;
  })();

  const availProjects = allProjects.filter(p => !rows.find(r => r.project_id === p.id) && `${p.name} ${p.code || ''}`.toLowerCase().includes(search.toLowerCase()));
  const availTenders  = allTenders.filter(t => !rows.find(r => r.tender_id === t.id) && `${t.title} ${t.reference_number || t.tender_ref || ''}`.toLowerCase().includes(search.toLowerCase()));

  const weekLabel = `${weekDates[0].toLocaleDateString('en-IN', { day:'2-digit', month:'short' })} – ${weekDates[5].toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}`;

  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'40vh', color: theme.textMuted }}>Loading timesheet…</div>;

  return (
    <div>
      <style>{`.ts-select:focus { border-color: ${theme.primary} !important; box-shadow: 0 0 0 2px rgba(249,115,22,0.15); }`}</style>

      {/* Week nav + Save */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1.25rem', flexWrap:'wrap', gap:'0.75rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
          <button onClick={() => setWeekOffset(w => w - 1)} style={navBtn}>‹ Prev</button>
          <div style={{ textAlign:'center' }}>
            <div style={{ fontWeight:700, fontSize:'0.95rem', color: theme.text }}>{weekLabel}</div>
            {weekOffset !== 0 && <button onClick={() => setWeekOffset(0)} style={{ background:'none', border:'none', color: theme.primary, fontSize:'0.75rem', cursor:'pointer', padding:0, fontWeight:600 }}>← Current week</button>}
          </div>
          <button onClick={() => setWeekOffset(w => w + 1)} disabled={weekOffset >= 0} style={{ ...navBtn, opacity: weekOffset >= 0 ? 0.4 : 1 }}>Next ›</button>
        </div>
        <div style={{ display:'flex', gap:'0.75rem', alignItems:'center', flexWrap:'wrap' }}>
          {/* Week status badge */}
          {wsStatus === 'draft'    && grandTotal > 0 && <span style={{ background:'#F3F4F6', color:'#374151', padding:'0.25rem 0.75rem', borderRadius:'1rem', fontSize:'0.75rem', fontWeight:700 }}>📝 Draft</span>}
          {wsStatus === 'approved' && <span style={{ background:'#DCFCE7', color:'#166534', padding:'0.25rem 0.75rem', borderRadius:'1rem', fontSize:'0.75rem', fontWeight:700, display:'inline-flex', alignItems:'center', gap:'0.3rem' }}>✅ Approved{weekStatus?.approved_by_name && <span style={{ fontWeight:400, opacity:0.8 }}>· {weekStatus.approved_by_name}</span>}</span>}
          {wsStatus === 'submitted' && <span style={{ background:'#DBEAFE', color:'#1D4ED8', padding:'0.25rem 0.75rem', borderRadius:'1rem', fontSize:'0.75rem', fontWeight:700 }}>⏳ Pending Approval</span>}
          {wsStatus === 'rejected' && <span style={{ background:'#FEE2E2', color:'#DC2626', padding:'0.25rem 0.75rem', borderRadius:'1rem', fontSize:'0.75rem', fontWeight:700, display:'inline-flex', alignItems:'center', gap:'0.3rem' }}>❌ Rejected{weekStatus?.approved_by_name && <span style={{ fontWeight:400, opacity:0.8 }}>· {weekStatus.approved_by_name}</span>}</span>}
          {isLocked && <span style={{ background:'#FEF2F2', color:'#DC2626', padding:'0.25rem 0.75rem', borderRadius:'1rem', fontSize:'0.75rem', fontWeight:700 }}>🔒 Locked</span>}
          {isOver40h && !isLocked && <span style={{ background:'#FEF3C7', color:'#92400E', padding:'0.25rem 0.75rem', borderRadius:'1rem', fontSize:'0.75rem', fontWeight:700 }}>⚠️ {grandTotal}h / 40h max</span>}
          {!isLocked && !isApproved && (
            <button onClick={handleSave} disabled={saving || isOver40h}
              style={{ background: saving ? '#FDBA74' : isOver40h ? '#D1D5DB' : '#374151', color:'#fff', border:'none', borderRadius:'0.5rem', padding:'0.6rem 1.4rem', fontWeight:600, fontSize:'0.875rem', cursor: (saving || isOver40h) ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Saving…' : '📝 Save Draft'}
            </button>
          )}
          {!isLocked && !isApproved && !isSubmitted && grandTotal > 0 && (
            <button onClick={canSubmitNow ? handleSubmit : undefined}
              disabled={submitting || isOver40h || !canSubmitNow}
              title={!canSubmitNow ? `Submit opens Friday 6:00 PM` : ''}
              style={{
                background: submitting ? '#93C5FD' : !canSubmitNow ? '#9CA3AF' : '#1D4ED8',
                color:'#fff', border:'none', borderRadius:'0.5rem', padding:'0.6rem 1.4rem',
                fontWeight:600, fontSize:'0.875rem',
                cursor: (submitting || !canSubmitNow) ? 'not-allowed' : 'pointer',
                opacity: !canSubmitNow ? 0.7 : 1,
              }}>
              {submitting ? 'Submitting…' : '📤 Submit for Approval'}
            </button>
          )}
        </div>
      </div>

      {saveMsg && <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:'0.5rem', padding:'0.7rem 1rem', marginBottom:'1rem', color:'#166534', fontSize:'0.875rem' }}>✓ {saveMsg}</div>}
      {error   && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'0.5rem', padding:'0.7rem 1rem', marginBottom:'1rem', color:'#DC2626',  fontSize:'0.875rem' }}>{error}</div>}
      {!isLocked && !isApproved && !isSubmitted && grandTotal > 0 && !canSubmitNow && (
        <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:'0.5rem', padding:'0.6rem 1rem', marginBottom:'1rem', color:'#92400E', fontSize:'0.8rem', display:'flex', alignItems:'center', gap:'0.5rem' }}>
          <span>🕕</span>
          <span>
            Submission opens <strong>Friday 6:00 PM</strong>.
            Please submit your timesheet before end of the week.
            Opens: {submitOpensAt.toLocaleDateString('en-IN', { weekday:'short', day:'2-digit', month:'short' })} at 6:00 PM.
          </span>
        </div>
      )}
      {wsStatus === 'rejected' && weekStatus?.rejection_reason && (
        <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'0.5rem', padding:'0.7rem 1rem', marginBottom:'1rem', color:'#DC2626', fontSize:'0.875rem' }}>
          ❌ Rejected: {weekStatus.rejection_reason} — Please correct and re-submit.
        </div>
      )}

      {/* Grid */}
      <div style={{ background:'#fff', borderRadius:'0.75rem', boxShadow:'0 1px 6px rgba(0,0,0,0.08)', border:`1px solid ${theme.border}`, overflowX:'auto' }}>
        <table style={{ borderCollapse:'collapse', width:'100%', minWidth:'820px' }}>
          <colgroup>
            <col style={{ width:'80px' }} /><col style={{ width:'36px' }} /><col style={{ width:'220px' }} />
            {weekDates.map((_, i) => <col key={i} style={{ width:'82px' }} />)}
            <col style={{ width:'72px' }} />{!isLocked && <col style={{ width:'36px' }} />}
          </colgroup>
          <thead>
            <tr style={{ background: theme.dark }}>
              <th style={{ ...TH, position:'sticky', left:0, zIndex:3, background:theme.dark, textAlign:'left', borderRight:`1px solid rgba(255,255,255,0.08)` }}>Ref</th>
              <th style={{ ...TH, background:theme.dark }}></th>
              <th style={{ ...TH, position:'sticky', left:116, zIndex:3, background:theme.dark, textAlign:'left', borderRight:`2px solid ${theme.primary}` }}>Name / Title</th>
              {weekDates.map((d, i) => {
                const isToday = d.getTime() === today.getTime();
                return (
                  <th key={i} style={{ ...TH, background: isToday ? '#1E3A5F' : theme.dark, color: isToday ? '#93C5FD' : '#D1D5DB' }}>
                    <div style={{ fontSize:'0.68rem', fontWeight:600 }}>{DAY_LABELS[i]}</div>
                    <div style={{ fontSize:'0.85rem', fontWeight:700 }}>{d.getDate()}</div>
                  </th>
                );
              })}
              <th style={{ ...TH, background:'#111827', color:theme.primary, fontSize:'0.75rem' }}>Total</th>
              {!isLocked && <th style={{ ...TH, background:theme.dark }}></th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={10 + (!isLocked ? 1 : 0)} style={{ padding:'2.5rem', textAlign:'center', color:theme.textMuted, fontSize:'0.875rem' }}>
                No rows yet — click "+ Add Row" to start.
              </td></tr>
            )}
            {rows.map((row, ri) => {
              const bg = ri % 2 === 0 ? '#fff' : '#FAFAFA';
              const rt = rowTotal(row);
              const isTender = row.row_type === 'tender';
              return (
                <tr key={row.key} style={{ borderBottom:`1px solid ${theme.border}` }}>
                  <td style={{ ...TD, position:'sticky', left:0, zIndex:1, background:bg, borderRight:`1px solid ${theme.border}`, fontFamily:'monospace', fontSize:'0.72rem', color:theme.textMuted }}>
                    {isTender ? row.tender_ref : (row.project_code || '—')}
                  </td>
                  <td style={{ ...TD, padding:'0 0.2rem', background:bg, textAlign:'center' }}>
                    <span style={{ fontSize:'0.62rem', fontWeight:700, padding:'0.15rem 0.35rem', borderRadius:'0.3rem', background: isTender ? '#FEF3C7' : '#EEF2FF', color: isTender ? '#92400E' : '#4338CA' }}>
                      {isTender ? '🏷' : '📂'}
                    </span>
                  </td>
                  <td style={{ ...TD, position:'sticky', left:116, zIndex:1, background:bg, borderRight:`2px solid rgba(249,115,22,0.15)` }}>
                    <div style={{ fontSize:'0.85rem', fontWeight:500, color:theme.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {isTender ? row.tender_title : row.project_name}
                    </div>
                    {!row.isAssigned && <span style={{ fontSize:'0.62rem', color:'#9CA3AF', background:'#F3F4F6', borderRadius:'0.25rem', padding:'0 0.3rem' }}>added</span>}
                  </td>
                  {weekDates.map((d, di) => {
                    const dateStr  = toISO(d);
                    const val      = row.hours[dateStr] || '';
                    const isToday  = d.getTime() === today.getTime();
                    const isOnLeave = leaveDays.has(dateStr);
                    return (
                      <td key={di} style={{ ...TD, padding:'0.3rem 0.25rem', textAlign:'center', background: isOnLeave ? '#FEF2F2' : isToday ? '#FFF7ED' : bg }}>
                        {isLocked || isOnLeave ? (
                          isOnLeave
                            ? <span title="On approved leave" style={{ fontSize:'0.7rem', fontWeight:700, color:'#DC2626', background:'#FEE2E2', padding:'0.15rem 0.4rem', borderRadius:'0.3rem' }}>Leave</span>
                            : <span style={{ fontSize:'0.85rem', color: val ? theme.text : '#D1D5DB' }}>{val ? `${val}h` : '—'}</span>
                        ) : (
                          <select className="ts-select" value={val}
                            onChange={e => updateHours(row.key, dateStr, e.target.value === '' ? '' : Number(e.target.value))}
                            style={{ width:'100%', padding:'0.3rem 0.2rem', border:`1.5px solid ${val ? theme.primary : '#E5E7EB'}`, borderRadius:'0.375rem', fontSize:'0.82rem', color: val ? '#111827' : '#9CA3AF', background: val ? '#FFF7ED' : '#F9FAFB', cursor:'pointer', outline:'none', textAlign:'center' }}>
                            <option value="">—</option>
                            {HOUR_OPTIONS.map(h => <option key={h} value={h}>{h}h</option>)}
                          </select>
                        )}
                      </td>
                    );
                  })}
                  <td style={{ ...TD, textAlign:'center', fontWeight:700, color: rt > 0 ? theme.primary : '#D1D5DB', fontSize:'0.875rem', background:bg }}>{rt > 0 ? `${rt}h` : '—'}</td>
                  {!isLocked && (
                    <td style={{ ...TD, textAlign:'center', padding:'0.25rem', background:bg }}>
                      {!row.isAssigned && (
                        <button onClick={() => removeRow(row.key)} style={{ background:'none', border:'none', cursor:'pointer', color:'#EF4444', fontSize:'1.1rem', lineHeight:1, padding:'0.15rem 0.35rem' }}>×</button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            {/* Totals row */}
            <tr style={{ background:'#F1F5F9', borderTop:`2px solid ${theme.border}` }}>
              <td colSpan={3} style={{ ...TD, position:'sticky', left:0, zIndex:1, background:'#F1F5F9', borderRight:`2px solid rgba(249,115,22,0.2)`, fontWeight:700, fontSize:'0.85rem', color:theme.text }}>Total Hours</td>
              {weekDates.map((d, i) => {
                const ds = toISO(d); const t2 = dayTotal(ds); const isToday = d.getTime() === today.getTime();
                const isOnLeave = leaveDays.has(ds);
                return (
                  <td key={i} style={{ ...TD, textAlign:'center', fontWeight:700, fontSize:'0.875rem', background: isOnLeave ? '#FEE2E2' : isToday ? '#FFF3E0' : '#F1F5F9', color: isOnLeave ? '#DC2626' : t2 > 8 ? '#DC2626' : t2 > 0 ? '#166534' : '#D1D5DB' }}>
                    {isOnLeave ? 'Leave' : t2 > 0 ? `${t2}h` : '—'}
                  </td>
                );
              })}
              <td style={{ ...TD, textAlign:'center', fontWeight:800, fontSize:'0.95rem', color:theme.primary, background:'#F1F5F9' }}>{grandTotal > 0 ? `${grandTotal}h` : '—'}</td>
              {!isLocked && <td style={{ background:'#F1F5F9' }}></td>}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Add Row */}
      {!isLocked && (
        <div style={{ marginTop:'1rem', position:'relative', display:'inline-block' }}>
          <button onClick={() => setShowAdd(v => !v)}
            style={{ background:'#fff', border:`1.5px dashed #D1D5DB`, borderRadius:'0.5rem', padding:'0.5rem 1.1rem', color:'#6B7280', fontSize:'0.85rem', cursor:'pointer', fontWeight:500 }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.color = theme.primary; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#D1D5DB'; e.currentTarget.style.color = '#6B7280'; }}>
            + Add Row
          </button>
          {showAdd && (
            <div style={{ position:'absolute', top:'110%', left:0, background:'#fff', border:`1px solid ${theme.border}`, borderRadius:'0.5rem', boxShadow:'0 8px 24px rgba(0,0,0,0.12)', width:'320px', zIndex:100, overflow:'hidden' }}>
              {isSales && (
                <div style={{ display:'flex', borderBottom:`1px solid ${theme.border}` }}>
                  {['project','tender'].map(tab => (
                    <button key={tab} onClick={() => { setAddTab(tab); setSearch(''); }}
                      style={{ flex:1, padding:'0.55rem', border:'none', cursor:'pointer', fontSize:'0.8rem', fontWeight:600, background: addTab === tab ? theme.lightBg : '#fff', color: addTab === tab ? theme.primary : theme.textMuted, borderBottom: addTab === tab ? `2px solid ${theme.primary}` : '2px solid transparent' }}>
                      {tab === 'project' ? '📂 Projects' : '🏷 Tenders'}
                    </button>
                  ))}
                </div>
              )}
              <div style={{ padding:'0.65rem' }}>
                <input autoFocus placeholder={addTab === 'tender' ? 'Search tenders…' : 'Search projects…'} value={search} onChange={e => setSearch(e.target.value)}
                  style={{ width:'100%', boxSizing:'border-box', padding:'0.45rem 0.75rem', border:`1px solid ${theme.border}`, borderRadius:'0.375rem', fontSize:'0.85rem', outline:'none' }} />
              </div>
              <div style={{ maxHeight:'240px', overflowY:'auto' }}>
                {(addTab === 'tender' && isSales) ? (
                  availTenders.length === 0 ? <div style={{ padding:'1rem', textAlign:'center', color:theme.textMuted, fontSize:'0.85rem' }}>No tenders found</div>
                    : availTenders.map(t => (
                      <button key={t.id} onClick={() => addTenderRow(t)}
                        style={{ width:'100%', padding:'0.55rem 1rem', background:'none', border:'none', cursor:'pointer', textAlign:'left', borderBottom:`1px solid #F3F4F6`, display:'block' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#FFF7ED'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        <div style={{ fontSize:'0.82rem', fontWeight:600, color:theme.text }}>{t.title}</div>
                        <div style={{ fontSize:'0.7rem', color:theme.textMuted }}>{t.reference_number || t.tender_ref} {t.client_name ? `· ${t.client_name}` : ''}</div>
                      </button>
                    ))
                ) : (
                  availProjects.length === 0 ? <div style={{ padding:'1rem', textAlign:'center', color:theme.textMuted, fontSize:'0.85rem' }}>No projects found</div>
                    : availProjects.map(p => (
                      <button key={p.id} onClick={() => addProjectRow(p)}
                        style={{ width:'100%', padding:'0.55rem 1rem', background:'none', border:'none', cursor:'pointer', textAlign:'left', borderBottom:`1px solid #F3F4F6`, display:'block' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        <div style={{ fontSize:'0.85rem', fontWeight:500, color:theme.text }}>{p.name}</div>
                        <div style={{ fontSize:'0.7rem', color:theme.textMuted }}>{p.code}</div>
                      </button>
                    ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
      <p style={{ marginTop:'1.25rem', fontSize:'0.75rem', color:'#9CA3AF' }}>
        {isLocked ? '🔒 Locked — submissions only allowed Mon–Sat of the current week.'
          : `💡 Log hours for each day. Timesheet locks after Saturday of this week.`}
      </p>
    </div>
  );
}

const navBtn = { padding:'0.35rem 0.85rem', background:'#fff', border:`1.5px solid #E5E7EB`, borderRadius:'0.5rem', fontSize:'0.82rem', fontWeight:600, color:'#374151', cursor:'pointer' };

// ─── History tab ──────────────────────────────────────────────────────────────
function History() {
  const [period,    setPeriod]    = useState('weekly');
  const [dateFrom,  setDateFrom]  = useState('');
  const [dateTo,    setDateTo]    = useState('');
  const [projectId, setProjectId] = useState('');
  const [projects,  setProjects]  = useState([]);
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');

  useEffect(() => {
    hrms.getProjects().then(r => setProjects(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = { period };
      if (period === 'custom' && dateFrom) params.date_from = dateFrom;
      if (period === 'custom' && dateTo)   params.date_to   = dateTo;
      if (projectId) params.project_id = projectId;
      const res = await hrms.getTimesheetHistory(params);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load history.');
    } finally { setLoading(false); }
  }, [period, dateFrom, dateTo, projectId]);

  useEffect(() => { if (period !== 'custom') load(); }, [period, projectId]); // eslint-disable-line

  const inp = { padding:'0.45rem 0.75rem', border:`1.5px solid ${theme.border}`, borderRadius:'0.5rem', fontSize:'0.83rem', color:theme.text, background:'#fff', outline:'none' };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem' }}>

      {/* Filters */}
      <div style={{ background:'#fff', border:`1px solid ${theme.border}`, borderRadius:'0.75rem', padding:'1rem 1.25rem', display:'flex', flexWrap:'wrap', gap:'0.75rem', alignItems:'flex-end' }}>
        {/* Period pills */}
        <div style={{ display:'flex', gap:'0.35rem', flexWrap:'wrap' }}>
          {PERIODS.map(p => (
            <button key={p.key} onClick={() => setPeriod(p.key)} style={{
              padding:'0.35rem 0.9rem', borderRadius:'999px', fontSize:'0.8rem', fontWeight:600, cursor:'pointer',
              border:`1.5px solid ${period === p.key ? theme.primary : theme.border}`,
              background: period === p.key ? theme.primary : '#F9FAFB',
              color: period === p.key ? '#fff' : theme.textMuted,
              transition:'all 0.15s',
            }}>{p.label}</button>
          ))}
        </div>

        {/* Custom date range */}
        {period === 'custom' && (
          <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', flexWrap:'wrap' }}>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ ...inp, width:140 }} />
            <span style={{ color:theme.textMuted, fontSize:'0.8rem' }}>→</span>
            <input type="date" value={dateTo}   onChange={e => setDateTo(e.target.value)}   style={{ ...inp, width:140 }} />
            <button onClick={load} style={{ padding:'0.4rem 1rem', background:theme.primary, color:'#fff', border:'none', borderRadius:'0.5rem', fontSize:'0.82rem', fontWeight:700, cursor:'pointer' }}>Apply</button>
          </div>
        )}

        {/* Project filter */}
        <select value={projectId} onChange={e => setProjectId(e.target.value)} style={{ ...inp, minWidth:170 }}>
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {error && <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'0.5rem', padding:'0.75rem 1rem', color:'#DC2626', fontSize:'0.85rem' }}>{error}</div>}

      {loading && (
        <div style={{ display:'flex', justifyContent:'center', padding:'3rem' }}>
          <div style={{ width:32, height:32, border:`3px solid ${theme.border}`, borderTop:`3px solid ${theme.primary}`, borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      )}

      {!loading && data && (
        <>
          {/* KPI cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px, 1fr))', gap:'0.875rem' }}>
            {[
              { label:'Total Hours',  value:`${fmt(data.total_hours)}h`, color:'#3B82F6', bg:'#EFF6FF' },
              { label:'Days Logged',  value:data.total_days,             color:'#10B981', bg:'#F0FDF4' },
              { label:'Entries',      value:data.entries?.length || 0,   color:'#F97316', bg:'#FFF7ED' },
              { label:'Projects',     value:data.by_project?.length || 0,color:'#8B5CF6', bg:'#F5F3FF' },
            ].map((k,i) => (
              <div key={i} style={{ background:k.bg, border:`1px solid ${k.color}20`, borderRadius:'0.75rem', padding:'0.875rem 1rem' }}>
                <p style={{ fontSize:'0.68rem', fontWeight:700, color:k.color, textTransform:'uppercase', letterSpacing:'0.05em', margin:'0 0 0.25rem' }}>{k.label}</p>
                <p style={{ fontSize:'1.75rem', fontWeight:800, color:k.color, margin:0, lineHeight:1 }}>{k.value}</p>
              </div>
            ))}
          </div>

          {/* Charts */}
          {(data.by_project?.length > 0 || data.by_date?.length > 0) && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1.25rem' }}>
              {/* Hours by project */}
              <div style={{ background:'#fff', border:`1px solid ${theme.border}`, borderRadius:'0.75rem', padding:'1.1rem 1.25rem' }}>
                <p style={{ fontSize:'0.85rem', fontWeight:700, color:theme.text, margin:'0 0 1rem' }}>Hours by Project</p>
                {data.by_project?.length ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data.by_project.slice(0,10)} margin={{ top:4, right:8, bottom:36, left:-10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                      <XAxis dataKey="name" tick={{ fontSize:10, fill:theme.textMuted }} angle={-20} textAnchor="end" interval={0} />
                      <YAxis tick={{ fontSize:11, fill:theme.textMuted }} />
                      <Tooltip formatter={v => [`${v}h`,'Hours']} contentStyle={{ fontSize:12, borderRadius:'0.5rem' }} />
                      <Bar dataKey="hours" radius={[4,4,0,0]}>
                        {data.by_project.slice(0,10).map((_,i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p style={{ color:theme.textMuted, textAlign:'center', padding:'2rem 0', fontSize:'0.83rem' }}>No data</p>}
              </div>

              {/* Hours by date */}
              <div style={{ background:'#fff', border:`1px solid ${theme.border}`, borderRadius:'0.75rem', padding:'1.1rem 1.25rem' }}>
                <p style={{ fontSize:'0.85rem', fontWeight:700, color:theme.text, margin:'0 0 1rem' }}>Daily Hours</p>
                {data.by_date?.length ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data.by_date} margin={{ top:4, right:8, bottom:36, left:-10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                      <XAxis dataKey="date" tick={{ fontSize:10, fill:theme.textMuted }} angle={-20} textAnchor="end" interval={0}
                        tickFormatter={d => { const dt=new Date(d); return `${dt.getDate()} ${dt.toLocaleString('en-IN',{month:'short'})}`; }} />
                      <YAxis tick={{ fontSize:11, fill:theme.textMuted }} />
                      <Tooltip formatter={v => [`${v}h`,'Hours']}
                        labelFormatter={d => new Date(d).toLocaleDateString('en-IN',{weekday:'short',day:'2-digit',month:'short'})}
                        contentStyle={{ fontSize:12, borderRadius:'0.5rem' }} />
                      <Bar dataKey="hours" fill={theme.primary} radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p style={{ color:theme.textMuted, textAlign:'center', padding:'2rem 0', fontSize:'0.83rem' }}>No data</p>}
              </div>
            </div>
          )}

          {/* Entries table */}
          <div style={{ background:'#fff', border:`1px solid ${theme.border}`, borderRadius:'0.75rem', overflow:'hidden' }}>
            <div style={{ padding:'0.875rem 1.25rem', borderBottom:`1px solid ${theme.border}`, background:'#F8FAFC', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <p style={{ fontWeight:700, fontSize:'0.88rem', color:theme.text, margin:0 }}>Timesheet Entries ({data.entries?.length || 0})</p>
              <p style={{ fontSize:'0.78rem', color:theme.textMuted, margin:0 }}>{data.date_from} → {data.date_to}</p>
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.83rem' }}>
                <thead>
                  <tr style={{ background:'#F9FAFB' }}>
                    {['Date','Type','Project / Tender','Description','Hours'].map(h => (
                      <th key={h} style={{ padding:'0.6rem 0.9rem', textAlign:'left', fontWeight:700, color:theme.textMuted, fontSize:'0.74rem', textTransform:'uppercase', letterSpacing:'0.04em', borderBottom:`1px solid ${theme.border}`, whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {!data.entries?.length ? (
                    <tr><td colSpan={5} style={{ padding:'2.5rem', textAlign:'center', color:theme.textMuted }}>No entries found for this period.</td></tr>
                  ) : data.entries.map((e, i) => (
                    <tr key={e.id} style={{ borderBottom:`1px solid ${theme.border}`, background: i%2===0 ? '#fff' : '#FAFAFA' }}>
                      <td style={{ padding:'0.6rem 0.9rem', color:theme.textMuted, whiteSpace:'nowrap' }}>
                        {new Date(e.date).toLocaleDateString('en-IN', { weekday:'short', day:'2-digit', month:'short', year:'2-digit' })}
                      </td>
                      <td style={{ padding:'0.6rem 0.9rem' }}>
                        <span style={{ padding:'2px 8px', borderRadius:9999, fontSize:'0.7rem', fontWeight:700, background: e.row_type==='tender' ? '#FEF3C7' : '#EEF2FF', color: e.row_type==='tender' ? '#92400E' : '#4338CA' }}>
                          {e.row_type==='tender' ? '🏷 Tender' : '📂 Project'}
                        </span>
                      </td>
                      <td style={{ padding:'0.6rem 0.9rem' }}>
                        <div style={{ fontWeight:600, color:theme.text }}>{e.row_type==='tender' ? (e.tender_title || e.tender_ref) : (e.project_name || '—')}</div>
                        <div style={{ fontSize:'0.71rem', color:theme.textMuted, fontFamily:'monospace' }}>{e.row_type==='tender' ? e.tender_ref : e.project_code}</div>
                      </td>
                      <td style={{ padding:'0.6rem 0.9rem', color:theme.textMuted, maxWidth:220, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.description || '—'}</td>
                      <td style={{ padding:'0.6rem 0.9rem', fontWeight:700, color:theme.primary, whiteSpace:'nowrap' }}>{fmt(e.hours)}h</td>
                    </tr>
                  ))}
                </tbody>
                {data.entries?.length > 0 && (
                  <tfoot>
                    <tr style={{ background:'#F0F9FF', borderTop:`2px solid ${theme.border}` }}>
                      <td colSpan={4} style={{ padding:'0.65rem 0.9rem', fontWeight:700, color:theme.text, fontSize:'0.83rem' }}>Total ({data.entries.length} entries)</td>
                      <td style={{ padding:'0.65rem 0.9rem', fontWeight:800, color:theme.primary, fontSize:'0.9rem' }}>{fmt(data.total_hours)}h</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── PM Approvals tab ─────────────────────────────────────────────────────────
function PMApprovals() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await hrms.getPendingApprovals();
      setPending(r.data || []);
    } catch { setPending([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (approvalId) => {
    try { await hrms.approveTimesheetProject(approvalId); load(); }
    catch (err) { alert(err?.response?.data?.detail || 'Failed to approve.'); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { alert('Rejection reason is required.'); return; }
    setSaving(true);
    try {
      await hrms.rejectTimesheetProject(rejectModal, rejectReason);
      setRejectModal(null); setRejectReason(''); load();
    } catch (err) { alert(err?.response?.data?.detail || 'Failed to reject.'); }
    finally { setSaving(false); }
  };

  const DAY_NAMES = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const fmtDate = d => { const dt = new Date(d + 'T00:00:00'); return dt.toLocaleDateString('en-IN', { day:'2-digit', month:'short' }); };

  // Group pending by week_start so each week gets its own dated header row
  const weekGroups = pending.reduce((acc, tw) => {
    (acc[tw.week_start] = acc[tw.week_start] || []).push(tw);
    return acc;
  }, {});

  const thStyle = { padding:'0.6rem 0.75rem', textAlign:'center', fontWeight:700, color:theme.textMuted, borderBottom:`2px solid ${theme.border}`, fontSize:'0.72rem', textTransform:'uppercase', whiteSpace:'nowrap', background:'#F8FAFC' };
  const tdC    = (extra={}) => ({ padding:'0.55rem 0.75rem', textAlign:'center', borderBottom:`1px solid #F3F4F6`, fontSize:'0.8rem', ...extra });
  const tdL    = (extra={}) => ({ padding:'0.55rem 0.75rem', textAlign:'left',   borderBottom:`1px solid #F3F4F6`, fontSize:'0.8rem', ...extra });

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <p style={{ fontWeight:700, color:theme.text, margin:0 }}>Pending Timesheet Approvals</p>
        <button onClick={load} style={{ background:'none', border:`1px solid ${theme.border}`, borderRadius:'0.375rem', padding:'0.3rem 0.75rem', fontSize:'0.78rem', cursor:'pointer', color:theme.textMuted }}>↻ Refresh</button>
      </div>

      {loading ? (
        <p style={{ color:theme.textMuted, textAlign:'center', padding:'2rem' }}>Loading…</p>
      ) : pending.length === 0 ? (
        <div style={{ textAlign:'center', padding:'3rem', color:theme.textMuted }}>
          <div style={{ fontSize:'2rem', marginBottom:'0.5rem' }}>✅</div>
          <p style={{ margin:0, fontWeight:600 }}>No pending approvals</p>
        </div>
      ) : Object.entries(weekGroups).map(([weekStart, group]) => {
        const days = group[0]?.days || [];
        return (
          <div key={weekStart} style={{ background:'#fff', border:`1px solid ${theme.border}`, borderRadius:'0.75rem', overflow:'hidden' }}>
            {/* Week header */}
            <div style={{ padding:'0.6rem 1rem', background:'#EEF2FF', borderBottom:`1px solid ${theme.border}`, display:'flex', alignItems:'center', gap:'1rem' }}>
              <span style={{ fontWeight:700, fontSize:'0.82rem', color:'#3730A3' }}>
                {weekStart} → {group[0]?.week_end}
              </span>
              <span style={{ background:'#C7D2FE', color:'#3730A3', fontSize:'0.72rem', fontWeight:700, padding:'0.15rem 0.5rem', borderRadius:9999 }}>
                {group[0]?.month}
              </span>
            </div>

            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.8rem' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, textAlign:'left', minWidth:150 }}>Employee</th>
                    <th style={{ ...thStyle, textAlign:'left', minWidth:140 }}>Project</th>
                    {days.map((d, i) => (
                      <th key={d} style={{ ...thStyle, minWidth:72 }}>
                        {DAY_NAMES[i]}<br />
                        <span style={{ fontWeight:500, fontSize:'0.68rem' }}>{fmtDate(d)}</span>
                      </th>
                    ))}
                    <th style={{ ...thStyle, minWidth:70 }}>Total</th>
                    <th style={{ ...thStyle, minWidth:150 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {group.map(tw => {
                    const projs   = tw.projects || [];
                    const rowspan = Math.max(projs.length, 1);

                    const empCell = (
                      <td rowSpan={rowspan} style={{ ...tdL({ verticalAlign:'top', borderRight:`1px solid ${theme.border}`, background:'#FAFAFA', borderBottom:`2px solid ${theme.border}` }) }}>
                        <div style={{ fontWeight:600, color:theme.text }}>{tw.employee_name}</div>
                        <div style={{ fontFamily:'monospace', fontSize:'0.7rem', color:theme.textMuted }}>{tw.emp_code}</div>
                        <div style={{ fontSize:'0.68rem', color:theme.textMuted, marginTop:'0.2rem' }}>
                          {tw.submitted_at ? new Date(tw.submitted_at).toLocaleDateString('en-IN') : ''}
                        </div>
                      </td>
                    );

                    return projs.length === 0 ? (
                      <tr key={tw.id}>
                        {empCell}
                        <td colSpan={days.length + 3} style={tdC({ color:theme.textMuted })}>No timesheet entries for your projects</td>
                      </tr>
                    ) : (
                      <>
                        {projs.map((p, pi) => (
                          <tr key={`${tw.id}-${pi}`} style={{ background: pi % 2 === 0 ? '#fff' : '#FAFFFE' }}>
                            {pi === 0 && empCell}
                            <td style={tdL({ fontWeight:500, color:theme.text, borderRight:`1px solid #F3F4F6` })}>
                              {p.project_name || '—'}
                            </td>
                            {days.map(d => {
                              const h = p.hours_by_day?.[d] || 0;
                              return (
                                <td key={d} style={tdC({ color: h > 0 ? '#2563EB' : '#D1D5DB', fontWeight: h > 0 ? 700 : 400 })}>
                                  {h > 0 ? `${h.toFixed(1)}h` : '—'}
                                </td>
                              );
                            })}
                            <td style={tdC({ fontWeight:700, color:'#7C3AED' })}>
                              {p.total > 0 ? `${p.total.toFixed(1)}h` : '—'}
                            </td>
                            {/* Per-project Approve / Reject */}
                            <td style={tdL({ whiteSpace:'nowrap' })}>
                              <div style={{ display:'flex', gap:'0.35rem' }}>
                                <button onClick={() => handleApprove(p.approval_id)}
                                  style={{ padding:'0.25rem 0.6rem', background:'#DCFCE7', color:'#166534', border:'none', borderRadius:'0.375rem', fontSize:'0.73rem', fontWeight:700, cursor:'pointer' }}>
                                  ✓ Approve
                                </button>
                                <button onClick={() => { setRejectModal(p.approval_id); setRejectReason(''); }}
                                  style={{ padding:'0.25rem 0.6rem', background:'#FEE2E2', color:'#DC2626', border:'none', borderRadius:'0.375rem', fontSize:'0.73rem', fontWeight:700, cursor:'pointer' }}>
                                  ✗ Reject
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Reject modal */}
      {rejectModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:'0.75rem', padding:'1.5rem', width:'400px', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin:'0 0 1rem', fontSize:'1rem', fontWeight:700, color:theme.text }}>Reject Timesheet</h3>
            <label style={{ fontSize:'0.8rem', fontWeight:600, color:theme.text, display:'block', marginBottom:'0.35rem' }}>Reason *</label>
            <textarea style={{ width:'100%', boxSizing:'border-box', padding:'0.6rem', border:`1.5px solid ${theme.border}`, borderRadius:'0.5rem', fontSize:'0.875rem', resize:'vertical', minHeight:'80px', outline:'none' }}
              value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Reason for rejection…" />
            <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end', marginTop:'1rem' }}>
              <button onClick={() => setRejectModal(null)} style={{ padding:'0.5rem 1rem', background:'#F3F4F6', border:'none', borderRadius:'0.5rem', cursor:'pointer', fontSize:'0.875rem', fontWeight:600 }}>Cancel</button>
              <button onClick={handleReject} disabled={saving || !rejectReason.trim()}
                style={{ padding:'0.5rem 1rem', background:'#EF4444', color:'#fff', border:'none', borderRadius:'0.5rem', cursor:'pointer', fontSize:'0.875rem', fontWeight:600, opacity: (!rejectReason.trim() || saving) ? 0.5 : 1 }}>
                {saving ? 'Rejecting…' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
const PM_ROLES = ['super_admin','project_manager','project_lead','team_lead','hr_admin','hr_head','ceo','cto','cfo'];

export default function MyTimesheet() {
  const { user } = useAuth();
  const isPM = PM_ROLES.includes(user?.role) || user?.is_team_lead;
  const [tab, setTab] = useState('log');

  const tabBtn = active => ({
    padding: '0.5rem 1.4rem',
    borderRadius: '0.5rem',
    fontWeight: 700,
    fontSize: '0.875rem',
    border: 'none',
    cursor: 'pointer',
    background: active ? theme.primary : 'transparent',
    color: active ? '#fff' : theme.textMuted,
    transition: 'all 0.15s',
  });

  return (
    <div style={{ fontFamily:"'Inter',-apple-system,sans-serif" }}>
      {/* Page header */}
      <div style={{ marginBottom:'1.25rem' }}>
        <h1 style={{ fontSize:'1.4rem', fontWeight:800, color:theme.text, margin:0, letterSpacing:'-0.02em' }}>My Timesheet</h1>
        <p style={{ fontSize:'0.82rem', color:theme.textMuted, margin:'0.2rem 0 0' }}>Log work hours and view your history</p>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:'0.25rem', background:'#F1F5F9', borderRadius:'0.625rem', padding:'0.25rem', marginBottom:'1.5rem', width:'fit-content' }}>
        <button onClick={() => setTab('log')}     style={tabBtn(tab === 'log')}>📝 Log Hours</button>
        <button onClick={() => setTab('history')} style={tabBtn(tab === 'history')}>📊 History</button>
        {isPM && <button onClick={() => setTab('approvals')} style={tabBtn(tab === 'approvals')}>✅ Approvals</button>}
      </div>

      {tab === 'log'       && <LogHours user={user} />}
      {tab === 'history'   && <History />}
      {tab === 'approvals' && isPM && <PMApprovals />}
    </div>
  );
}
